import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useGetProductByIdQuery } from '../../../store/api/productsApi'
import { useGetIngredientsQuery } from '../../../store/api/ingredientsApi'
import { IProduct } from '../../../types/products'
import { IIngredient, ISelectedIngredient } from '../../../types/ingredients'
import { Button, Checkbox, FormControlLabel } from '@mui/material'
import QuantityInput from '../../../components/quantity-input/QuantityInput'
import Loading from '../../../assets/icons/loading.svg?react'
import { useTranslation } from 'react-i18next'
import { serverUrl } from '../../../request'
import i18next from 'i18next'
import './ProductViewMobile.scss'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowRight } from '@fortawesome/free-solid-svg-icons'
import { getStorageKey } from '../../../utils/appType'

type Props = {
  setCart: React.Dispatch<React.SetStateAction<IProduct[]>>
  cart: IProduct[]
}

const c = 'product-view-mobile'

const ProductViewMobile = (props: Props) => {
  const [quantity, setQuantity] = useState(1)
  const [selectedIngredients, setSelectedIngredients] = useState<
    ISelectedIngredient[]
  >([])
  const { id } = useParams()
  const { t } = useTranslation()
  const { data, isLoading: loading } = useGetProductByIdQuery(String(id))
  const { data: allIngredients } = useGetIngredientsQuery()
  const splitDescription = data?.description.split('_')
  const splitDescriptionRu = data?.ru_description.split('_')

  const availableIngredients =
    allIngredients?.filter((ing: IIngredient) =>
      data?.availableIngredients?.includes(ing._id),
    ) || []

  const ingredientsTotal = selectedIngredients.reduce(
    (sum, ing) => sum + ing.price,
    0,
  )

  function toggleIngredient(ingredient: IIngredient) {
    const ingredientCustomerPrice = ingredient.customer_price ?? 0

    setSelectedIngredients((prev) => {
      const exists = prev.find((i) => i._id === ingredient._id)
      if (exists) {
        return prev.filter((i) => i._id !== ingredient._id)
      }
      return [
        ...prev,
        {
          _id: ingredient._id,
          label: ingredient.label,
          label_ru: ingredient.label_ru,
          price: ingredientCustomerPrice,
        },
      ]
    })
  }

  function getBasePrice() {
    if (!data) return 0
    return data.discount
      ? Math.round(
          Number(data.price) -
            (Number(data.price) * Number(data.discount)) / 100,
        )
      : Number(data.price)
  }

  function addToCart() {
    const basePrice = getBasePrice()
    const params = {
      ...data,
      quantity,
      price: String(basePrice + ingredientsTotal),
      selectedIngredients:
        selectedIngredients.length > 0 ? selectedIngredients : undefined,
    } as IProduct

    const itemIndex = props.cart.findIndex(
      (cartItem: IProduct) => cartItem._id === data?._id,
    )

    if (itemIndex === -1) {
      props.setCart([...props.cart, params])
      localStorage.setItem(
        getStorageKey('cart'),
        JSON.stringify([...props.cart, params]),
      )
    } else {
      const updatedCart = [...props.cart]

      updatedCart[itemIndex].quantity! += quantity
      updatedCart[itemIndex].selectedIngredients =
        selectedIngredients.length > 0 ? selectedIngredients : undefined
      updatedCart[itemIndex].price = String(basePrice + ingredientsTotal)
      localStorage.setItem(getStorageKey('cart'), JSON.stringify(updatedCart))
      props.setCart(updatedCart)
    }
  }

  return (
    <div className={c}>
      <div className={`${c}__card`}>
        {loading ? (
          <Loading />
        ) : (
          <React.Fragment>
            <div className={`${c}__card-image`}>
              <div className={`${c}__card-image-props`}>
                {data?.isNew && (
                  <div className={`${c}__card-image-props-prop`}>
                    {t('NEW')}
                  </div>
                )}
                {data?.discount && (
                  <div className={`${c}__card-image-props-prop`}>
                    -{data.discount}%
                  </div>
                )}
              </div>
              <img
                src={serverUrl + data?.image}
                alt=''
                onError={({ currentTarget }) => {
                  currentTarget.onerror = null

                  currentTarget.src = '/logo_bg.png'
                }}
              />
            </div>
            <div className={`${c}__card-content`}>
              <div className={`${c}__card-content-info`}>
                <div className={`${c}__card-content-info-title`}>
                  {i18next.language === 'ru' && data?.ru_name
                    ? data?.ru_name
                    : data?.name}
                </div>
                <div className={`${c}__card-content-info-description`}>
                  {i18next.language === 'ru' && data?.ru_description
                    ? splitDescriptionRu?.map((item, index: number) => (
                        <div key={index}>{item.trim()}</div>
                      ))
                    : splitDescription?.map((item, index: number) => (
                        <div key={index}>{item.trim()}</div>
                      ))}
                </div>
              </div>
              <div className={`${c}__card-content-cart`}>
                <div className={`${c}__card-content-cart-weight`}>
                  {data?.weight ? `${data?.weight}g` : ''}{' '}
                  {data?.pieces ? ` - ${data?.pieces} ${t('PIECES')}` : ''}
                </div>
                {availableIngredients.length > 0 && (
                  <div className={`${c}__card-content-cart-ingredients`}>
                    <div
                      className={`${c}__card-content-cart-ingredients-title`}
                    >
                      {t('ADDITIONAL_INGREDIENTS')}
                    </div>
                    <div className={`${c}__card-content-cart-ingredients-list`}>
                      {availableIngredients.map((ingredient: IIngredient) => (
                        <FormControlLabel
                          key={ingredient._id}
                          control={
                            <Checkbox
                              size='small'
                              checked={
                                !!selectedIngredients.find(
                                  (i) => i._id === ingredient._id,
                                )
                              }
                              onChange={() => toggleIngredient(ingredient)}
                              sx={{
                                '&.Mui-checked': { color: 'var(--color-primary)' },
                              }}
                            />
                          }
                          label={`${i18next.language === 'ru' && ingredient.label_ru ? ingredient.label_ru : ingredient.label} (+${ingredient.customer_price ?? 0} lei)`}
                        />
                      ))}
                    </div>
                  </div>
                )}
                <div className={`${c}__card-content-cart-price`}>
                  {data?.discount ? (
                    <div className={`${c}__card-content-cart-price-discount`}>
                      <div
                        className={`${c}__card-content-cart-price-discount-old`}
                      >
                        {data.price}
                      </div>
                      <div
                        className={`${c}__card-content-cart-price-discount-new`}
                      >
                        <FontAwesomeIcon width={12} icon={faArrowRight} />{' '}
                        {getBasePrice() + ingredientsTotal} lei
                      </div>
                    </div>
                  ) : (
                    `${getBasePrice() + ingredientsTotal} lei`
                  )}
                </div>
                <div className={`${c}__card-content-cart-add`}>
                  <Button
                    variant='contained'
                    onClick={addToCart}
                    disabled={!quantity}
                    sx={{
                      backgroundColor: 'var(--color-primary)',
                      textTransform: 'none',
                      fontWeight: 'bold',
                      width: '100%',
                      '&:hover': { backgroundColor: 'var(--color-primary-hover)' },
                    }}
                  >
                    {t('ADD_TO_CART')}
                  </Button>
                  <QuantityInput
                    setQuantity={setQuantity}
                    quantity={quantity}
                  />
                </div>
              </div>
            </div>
          </React.Fragment>
        )}
      </div>
    </div>
  )
}

export default ProductViewMobile
